import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { api, ApiError, isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import { PROJECT_STEP_IDS } from "pages/project/ProjectFormComponent/steps/projectSteps";
import swtAlert from "lib/sweetAlert";
import {
  projectStepHasIncompleteFileSelection,
  UPLOAD_ALL_FILES_MESSAGE,
  validateProjectBasics,
  validateProjectStep,
  validateProjectStepForNavigation,
} from "pages/project/validation/validateProjectStep";
import { buildProjectApiPayload, mapApiProjectToFormValues } from "utils/projectFormPayload";
import { FIXED_MAXIMUM_INVESTMENT_AMOUNT } from "config/envConfig";

export { FIXED_MAXIMUM_INVESTMENT_AMOUNT };

/** Form state keys aligned with `buildProjectApiPayload`. `imageUrl` is an array of upload rows; API receives it as an array of URL strings. */
export const PROJECT_FORM_INITIAL_VALUES = {
  title: "",
  issuerName: "",
  typeOfEligibleSecurityOffered: "",
  status: "draft",
  industry: "",
  campaignStartAt: "",
  campaignEndAt: "",
  totalFundsRequired: "",
  hardCap: "",
  minimumInvestmentAmount: "",
  maximumInvestmentAmount: FIXED_MAXIMUM_INVESTMENT_AMOUNT,
  sharePrice: "",
  description: "",
  imageUrl: [],
  companyWebsite: "",
  companyHeadOfficeAddress: "",
  companyPhone: "",
  companyEmail: "",
  companyOverview: "",
  teamMembers: [
    { id: "member-0", name: "", position: "", municipality: "", email: "", occupation: "", description: "", imageUrl: "", imagePending: "" },
  ],
  offeringDocuments: [],
  newsUpdates: [{ id: "news-0", updateTitle: "", date: "", updateDescription: "" }],
};

const ProjectFormContext = createContext(null);

function responseEnvelopeData(resp) {
  if (resp && typeof resp === "object" && Object.hasOwn(resp, "data")) {
    return resp.data;
  }
  return resp;
}

function extractCreatedProjectId(resp) {
  const root = responseEnvelopeData(resp);
  if (root == null || typeof root !== "object") return null;
  const raw = root.id ?? root._id ?? root.project?.id ?? root.project?._id;
  return raw == null ? null : String(raw);
}

function findFirstBlockingStepValidation(values) {
  const basicsErrors = {};
  validateProjectBasics(values, basicsErrors);
  if (Object.keys(basicsErrors).length > 0) {
    return {
      index: 0,
      stepId: "overview",
      errors: { ...validateProjectStep("overview", values), ...basicsErrors },
    };
  }

  for (let i = 0; i < PROJECT_STEP_IDS.length; i += 1) {
    const stepId = PROJECT_STEP_IDS[i];
    const stepErrors = validateProjectStep(stepId, values);
    if (Object.keys(stepErrors).length > 0) {
      return { index: i, stepId, errors: stepErrors };
    }
  }
  return null;
}

export function ProjectProvider({ children, existingProjectId = null }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [values, setValues] = useState(PROJECT_FORM_INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isLoadingProject, setIsLoadingProject] = useState(Boolean(existingProjectId));
  const [projectLoadError, setProjectLoadError] = useState("");

  const isEditMode = Boolean(existingProjectId);

  useEffect(() => {
    if (!existingProjectId) {
      setValues(PROJECT_FORM_INITIAL_VALUES);
      setProjectLoadError("");
      setIsLoadingProject(false);
      setCurrentStepIndex(0);
      setErrors({});
      return undefined;
    }

    let cancelled = false;
    setIsLoadingProject(true);
    setProjectLoadError("");

    (async () => {
      try {
        const resp = await api.get(API_ENDPOINTS.adminProjectById(existingProjectId));
        if (cancelled) return;
        if (!isSuccessResponse(resp)) {
          const msg = resp?.message || "Could not load project.";
          setProjectLoadError(msg);
          setValues(PROJECT_FORM_INITIAL_VALUES);
          return;
        }
        const patch = mapApiProjectToFormValues(resp);
        setValues({ ...PROJECT_FORM_INITIAL_VALUES, ...patch });
        setProjectLoadError("");
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof ApiError ? err.message : err?.message || "Could not load project.";
        setProjectLoadError(msg);
        setValues(PROJECT_FORM_INITIAL_VALUES);
      } finally {
        if (!cancelled) setIsLoadingProject(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [existingProjectId]);

  const currentStepId = PROJECT_STEP_IDS[currentStepIndex] ?? PROJECT_STEP_IDS[0];

  const clearFieldError = useCallback((name) => {
    setErrors((prev) => {
      const hasFieldError = Boolean(prev[name]);
      const hasTeamMemberErrors =
        name === "teamMembers" && (prev.teamMemberErrors || prev.teamMemberErrorsById);
      const hasNewsUpdateErrors =
        name === "newsUpdates" && (prev.newsUpdateErrors || prev.newsUpdateErrorsById);
      if (!hasFieldError && !hasTeamMemberErrors && !hasNewsUpdateErrors) return prev;
      const next = { ...prev };
      delete next[name];
      if (name === "teamMembers") {
        delete next.teamMemberErrors;
        delete next.teamMemberErrorsById;
      }
      if (name === "newsUpdates") {
        delete next.newsUpdateErrors;
        delete next.newsUpdateErrorsById;
      }
      return next;
    });
  }, []);

  const setField = useCallback(
    (name, value) => {
      if (name === "maximumInvestmentAmount") return;
      setValues((prev) => ({ ...prev, [name]: value }));
      clearFieldError(name);
      setSubmitError(null);
    },
    [clearFieldError]
  );

  const resetForm = useCallback(() => {
    setValues(PROJECT_FORM_INITIAL_VALUES);
    setCurrentStepIndex(0);
    setErrors({});
    setSubmitError(null);
  }, []);

  const validateAndSetErrorsForStep = useCallback(
    (stepId) => {
      const next = validateProjectStepForNavigation(stepId, values);
      setErrors(next);
      return { ok: Object.keys(next).length === 0, errors: next };
    },
    [values]
  );

  const maybeAlertIncompleteUploads = useCallback((stepId) => {
    if (!projectStepHasIncompleteFileSelection(stepId, values)) return;
    Promise.resolve(
      swtAlert.warning("Files not fully uploaded", UPLOAD_ALL_FILES_MESSAGE),
    ).catch(() => {});
  }, [values]);

  const goNext = useCallback(() => {
    const stepId = PROJECT_STEP_IDS[currentStepIndex];
    const { ok } = validateAndSetErrorsForStep(stepId);
    if (!ok) {
      maybeAlertIncompleteUploads(stepId);
      return false;
    }
    setCurrentStepIndex((i) => Math.min(i + 1, PROJECT_STEP_IDS.length - 1));
    return true;
  }, [currentStepIndex, validateAndSetErrorsForStep, maybeAlertIncompleteUploads]);

  const goBack = useCallback(() => {
    setErrors({});
    setSubmitError(null);
    setCurrentStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const goToStep = useCallback(
    (index) => {
      if (index < currentStepIndex) {
        setErrors({});
        setSubmitError(null);
        setCurrentStepIndex(index);
        return;
      }
      const stepId = PROJECT_STEP_IDS[currentStepIndex];
      const { ok } = validateAndSetErrorsForStep(stepId);
      if (!ok) {
        maybeAlertIncompleteUploads(stepId);
        return;
      }
      setCurrentStepIndex(Math.min(index, PROJECT_STEP_IDS.length - 1));
    },
    [currentStepIndex, validateAndSetErrorsForStep, maybeAlertIncompleteUploads]
  );

  const submitProject = useCallback(async () => {
    setSubmitError(null);

    const blocking = findFirstBlockingStepValidation(values);
    if (blocking) {
      setCurrentStepIndex(blocking.index);
      setErrors(blocking.errors);
      setSubmitError("Please fix the highlighted fields before submitting.");
      if (projectStepHasIncompleteFileSelection(blocking.stepId, values)) {
        Promise.resolve(
          swtAlert.warning("Files not fully uploaded", UPLOAD_ALL_FILES_MESSAGE),
        ).catch(() => {});
      }
      return { ok: false };
    }

    const payload = buildProjectApiPayload(values);
    setIsSubmitting(true);
    try {
      const resp = isEditMode
        ? await api.patch(API_ENDPOINTS.adminProjectById(existingProjectId), payload)
        : await api.post(API_ENDPOINTS.projects, payload);
      if (!isSuccessResponse(resp)) {
        const message =
          resp?.message ||
          (isEditMode ? "Could not update project." : "Could not create project.");
        setSubmitError(message);
        swtAlert.error(isEditMode ? "Error updating project" : "Error creating project", message);
        return { ok: false };
      }
      setValues(PROJECT_FORM_INITIAL_VALUES);
      setCurrentStepIndex(0);
      setErrors({});
      return { ok: true, createdId: extractCreatedProjectId(resp) };
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : err?.message || "Something went wrong. Try again.";
      setSubmitError(message);
      return { ok: false };
    } finally {
      setIsSubmitting(false);
    }
  }, [values, isEditMode, existingProjectId]);

  const value = useMemo(
    () => ({
      currentStepIndex,
      currentStepId,
      stepCount: PROJECT_STEP_IDS.length,
      values,
      setField,
      errors,
      isSubmitting,
      submitError,
      isEditMode,
      existingProjectId,
      isLoadingProject,
      projectLoadError,
      goNext,
      goBack,
      goToStep,
      submitProject,
      resetForm,
    }),
    [
      currentStepIndex,
      currentStepId,
      values,
      setField,
      errors,
      isSubmitting,
      submitError,
      isEditMode,
      existingProjectId,
      isLoadingProject,
      projectLoadError,
      goNext,
      goBack,
      goToStep,
      submitProject,
      resetForm,
    ]
  );

  return <ProjectFormContext.Provider value={value}>{children}</ProjectFormContext.Provider>;
}

ProjectProvider.propTypes = {
  children: PropTypes.node.isRequired,
  existingProjectId: PropTypes.string,
};

export function useProjectFormContext() {
  const ctx = useContext(ProjectFormContext);
  if (!ctx) {
    throw new Error("useProjectFormContext must be used within a ProjectProvider.");
  }
  return ctx;
}

export function useProjectContext() {
  return useProjectFormContext();
}
